// SPDX-FileCopyrightText: 2026 Mika Tammi / Pure Fun Solutions
// SPDX-License-Identifier: MIT OR Apache-2.0

// The 3D hero: the FuryCore mark seen in perspective. A packaged die whose
// top face is painted as the SoC floorplan, fan-out wires that carry the same
// ember sweep as the 2D mark, and the infurer flame, extruded, floating above
// the die. The camera orbits once a minute; the flame turns once every eight
// seconds, faster than the camera.
//
// Decoration only. The inline SVG mark is the first paint and stays the hero
// until this has drawn a frame. Every failure path returns early and leaves
// the SVG alone, so a missing GPU, a blocked script or an exception costs
// motion and never content. The constants follow the "3D hero constants" in
// infurer-brand/furycore/README.md.

import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

const COLOR = {
  wafer: "#0e0f12",
  substrate: "#16181d",
  trace: "#2a2d35",
  wire: "#323843",
  pin: "#3d434e",
  ember: "#e8410f",
  core: "#ffb627",
};

// Geometry in the 2D mark's own units (its 64-unit box, centred), so the 3D
// scene and every flat mark share one set of numbers: package 44 wide with a
// corner radius of 5, die 28 with 2.5, five pins a side, wires fanning in
// from each pin to a pad on the die edge.
const PACKAGE = 44;
const DIE = 28;
const PIN_C = [17, 24.5, 32, 39.5, 47].map((c) => c - 32);
const PAD_C = [21.5, 26.75, 32, 37.25, 42.5].map((c) => c - 32);
const PACKAGE_H = 3;
const DIE_H = 1.4;
const DIE_TOP = PACKAGE_H + DIE_H;
const FLAME_LIFT = 3.2; // air between the die and the flame's foot
const FLAME_Y = DIE_TOP + FLAME_LIFT + 11.25; // the flame's ink box is 22.5 tall

// Motion, in seconds.
const ORBIT_S = 60; // camera: one revolution a minute
const BOB_S = 23; // a slow rise and fall, out of step with the orbit
const SPIN_S = 8; // flame: one turn every eight seconds
const PULSE_S = 4.8; // the wires' ember sweep, as in the CSS of the 2D mark
const PULSE_STEP = 0.24; // per wire, clockwise from the top-left
const START = 0.6; // where the orbit starts: a little off the front

// The camera, framed so the package's diagonal fits at every orbit angle.
const FOV = 30;
const RADIUS = 136;
const ELEVATION = 0.66; // radians above the horizon
const BOB = 0.06;
const TARGET = new THREE.Vector3(0, 4, 0);

const art = document.getElementById("hero-art");

function boot() {
  if (!art) return;
  const outer = art.querySelector('[data-flame="outer"]');
  const inner = art.querySelector('[data-flame="core"]');
  if (!outer || !inner) return;
  if (navigator.connection && navigator.connection.saveData) return;

  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  const gl = canvas.getContext("webgl2", {
    alpha: true,
    antialias: true,
    premultipliedAlpha: true,
    powerPreference: "low-power",
  });
  if (!gl) return;

  try {
    run(canvas, gl, outer.getAttribute("d"), inner.getAttribute("d"));
  } catch {
    canvas.remove();
    art.classList.remove("is-3d");
  }
}

function run(canvas, gl, outerPath, corePath) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    context: gl,
    alpha: true,
    antialias: true,
  });
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.45;
  pmrem.dispose();

  const camera = new THREE.PerspectiveCamera(FOV, 1, 1, 1000);
  const aniso = renderer.capabilities.getMaxAnisotropy();

  // -- the package ----------------------------------------------------------
  const substrate = new THREE.MeshStandardMaterial({
    color: COLOR.substrate,
    roughness: 0.55,
    metalness: 0.05,
  });
  const pkg = slab(PACKAGE, 5, PACKAGE_H, 0.35, substrate);
  scene.add(pkg);

  // Gull-wing pins: out from the package's lower half, then down to the board.
  const pinMat = new THREE.MeshStandardMaterial({
    color: COLOR.pin,
    roughness: 0.32,
    metalness: 0.6,
  });
  const reach = 5.5;
  const arm = new THREE.InstancedMesh(new THREE.BoxGeometry(2.6, 0.7, reach), pinMat, 20);
  const foot = new THREE.InstancedMesh(new THREE.BoxGeometry(2.6, 1.9, 0.7), pinMat, 20);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const one = new THREE.Vector3(1, 1, 1);
  let n = 0;
  for (let side = 0; side < 4; side++) {
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), (side * Math.PI) / 2);
    for (const c of PIN_C) {
      const a = new THREE.Vector3(c, 1.15, -(PACKAGE / 2 + reach / 2)).applyQuaternion(q);
      const f = new THREE.Vector3(c, 0.2, -(PACKAGE / 2 + reach - 0.35)).applyQuaternion(q);
      arm.setMatrixAt(n, m.compose(a, q, one));
      foot.setMatrixAt(n, m.compose(f, q, one));
      n++;
    }
  }
  scene.add(arm, foot);

  // The fan-out wires, numbered clockwise from the top-left like the 2D
  // mark's --i, each with its own material so the ember sweep can run. The
  // top side (-z) is built and then turned a quarter at a time clockwise as
  // seen from above, which walks the right side top to bottom, the bottom
  // right to left and the left bottom to top: the 2D mark's order.
  const wires = [];
  const wireGeo = new THREE.BoxGeometry(0.62, 0.05, 1);
  for (let quarter = 0; quarter < 4; quarter++) {
    const turn = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      (-quarter * Math.PI) / 2,
    );
    for (let k = 0; k < PIN_C.length; k++) {
      const from = new THREE.Vector3(PIN_C[k], 0, -PACKAGE / 2 + 0.6);
      const to = new THREE.Vector3(PAD_C[k], 0, -DIE / 2 - 0.2);
      const dir = to.clone().sub(from);
      const mat = new THREE.MeshStandardMaterial({
        color: COLOR.wire,
        emissive: COLOR.ember,
        emissiveIntensity: 0,
        roughness: 0.45,
        metalness: 0.3,
      });
      const wire = new THREE.Mesh(wireGeo, mat);
      wire.scale.z = dir.length();
      wire.position.copy(from.clone().add(to).multiplyScalar(0.5));
      wire.position.y = PACKAGE_H + 0.03;
      wire.rotation.y = Math.atan2(dir.x, dir.z);
      wire.position.applyQuaternion(turn);
      wire.quaternion.premultiply(turn);
      wires.push(mat);
      scene.add(wire);
    }
  }

  // -- the die --------------------------------------------------------------
  const dieSide = new THREE.MeshPhysicalMaterial({
    color: COLOR.wafer,
    roughness: 0.35,
    metalness: 0.2,
    clearcoat: 1,
  });
  const die = slab(DIE, 2.5, DIE_H, 0.2, dieSide);
  die.position.y = PACKAGE_H;
  scene.add(die);

  const { base, glow } = dieShot(1024);
  for (const t of [base, glow]) {
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = aniso;
  }
  const top = new THREE.Mesh(
    new THREE.PlaneGeometry(DIE - 0.5, DIE - 0.5),
    new THREE.MeshPhysicalMaterial({
      map: base,
      emissive: COLOR.ember,
      emissiveMap: glow,
      emissiveIntensity: 1.5,
      roughness: 0.5,
      metalness: 0.1,
      clearcoat: 1,
      clearcoatRoughness: 0.3,
      // Keep the room's reflection a sheen, so the die stays dark silicon.
      envMapIntensity: 0.5,
    }),
  );
  top.rotation.x = -Math.PI / 2;
  top.position.y = DIE_TOP + 0.02;
  scene.add(top);

  // -- the flame ------------------------------------------------------------
  const flame = new THREE.Group();
  flame.position.y = FLAME_Y;
  const outerGeo = new THREE.ExtrudeGeometry(shapeFromPath(outerPath), {
    depth: 2.6,
    bevelEnabled: true,
    bevelThickness: 0.5,
    bevelSize: 0.45,
    bevelSegments: 4,
    curveSegments: 28,
  });
  outerGeo.translate(0, 0, -1.3);
  // The core is thicker than the flame round it, so it shows through on both
  // faces as the flame turns.
  const coreGeo = new THREE.ExtrudeGeometry(shapeFromPath(corePath), {
    depth: 3.4,
    bevelEnabled: true,
    bevelThickness: 0.45,
    bevelSize: 0.3,
    bevelSegments: 4,
    curveSegments: 28,
  });
  coreGeo.translate(0, 0, -1.7);
  flame.add(
    new THREE.Mesh(
      outerGeo,
      new THREE.MeshStandardMaterial({
        color: COLOR.ember,
        emissive: COLOR.ember,
        emissiveIntensity: 0.35,
        roughness: 0.42,
        metalness: 0.05,
      }),
    ),
    new THREE.Mesh(
      coreGeo,
      new THREE.MeshStandardMaterial({
        color: COLOR.core,
        emissive: COLOR.core,
        emissiveIntensity: 0.9,
        roughness: 0.35,
        metalness: 0.05,
      }),
    ),
  );
  scene.add(flame);

  // The flame lights the die: a warm point light inside it, plus a soft
  // additive glow. No post-processing, to keep phone GPUs cool. The light
  // sits in the flame's lower half, high enough that its reflection in the
  // glossy die stays a warm sheen instead of a white hotspot.
  const fire = new THREE.PointLight("#ff7a3d", 380, 0, 2);
  fire.position.set(0, FLAME_Y - 4, 0);
  scene.add(fire);
  const rim = new THREE.DirectionalLight("#9fb4ff", 0.28);
  rim.position.set(-40, 50, -60);
  scene.add(rim);

  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: radial(128, [
        [0, "rgba(232,65,15,0.5)"],
        [0.4, "rgba(232,65,15,0.16)"],
        [1, "rgba(232,65,15,0)"],
      ]),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    }),
  );
  halo.scale.set(34, 34, 1);
  halo.position.y = FLAME_Y;
  scene.add(halo);

  // A soft contact shadow, so the package sits on something.
  const shadow = new THREE.Mesh(
    new THREE.PlaneGeometry(90, 90),
    new THREE.MeshBasicMaterial({
      map: radial(256, [
        [0, "rgba(0,0,0,0.6)"],
        [0.45, "rgba(0,0,0,0.35)"],
        [1, "rgba(0,0,0,0)"],
      ]),
      transparent: true,
      depthWrite: false,
    }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -0.75;
  scene.add(shadow);

  // -- motion -----------------------------------------------------------------
  function pose(t) {
    const theta = START + (2 * Math.PI * t) / ORBIT_S;
    const phi = ELEVATION + BOB * Math.sin((2 * Math.PI * t) / BOB_S);
    camera.position.set(
      RADIUS * Math.cos(phi) * Math.sin(theta),
      TARGET.y + RADIUS * Math.sin(phi),
      RADIUS * Math.cos(phi) * Math.cos(theta),
    );
    camera.lookAt(TARGET);
    flame.rotation.y = START + (2 * Math.PI * t) / SPIN_S;
    for (let i = 0; i < wires.length; i++) {
      const phase = ((((t - i * PULSE_STEP) / PULSE_S) % 1) + 1) % 1;
      wires[i].emissiveIntensity = reduce ? 0 : 1.6 * pulse(phase);
    }
  }

  let t = 0;
  let last = 0;
  let running = false;
  let onScreen = true;

  function render() {
    pose(t);
    renderer.render(scene, camera);
  }

  // Anything that throws once the loop runs stops the loop and hands the hero
  // back to the SVG, as at boot: a broken frame never leaves the mark hidden.
  function giveUp() {
    running = false;
    renderer.setAnimationLoop(null);
    art.classList.remove("is-3d");
  }

  function frame(now) {
    t += Math.min((now - last) / 1000, 0.1);
    last = now;
    try {
      render();
    } catch {
      giveUp();
    }
  }

  function sync() {
    const want = !reduce && onScreen && !document.hidden;
    if (want && !running) {
      running = true;
      last = performance.now();
      renderer.setAnimationLoop(frame);
    } else if (!want && running) {
      running = false;
      renderer.setAnimationLoop(null);
    }
  }

  function resize() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, w < 480 ? 1.5 : 2));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    if (!running) render();
  }

  art.appendChild(canvas);
  resize();
  render();
  // The swap waits for a drawn frame: until then the SVG is the hero.
  requestAnimationFrame(() => art.classList.add("is-3d"));

  new ResizeObserver(resize).observe(canvas);
  new IntersectionObserver((entries) => {
    onScreen = entries.some((e) => e.isIntersecting);
    sync();
  }).observe(art);
  document.addEventListener("visibilitychange", sync);
  canvas.addEventListener("webglcontextlost", (e) => {
    e.preventDefault();
    giveUp();
  });
  sync();
}

// The CSS sweep of the 2D mark, as an intensity: up to full at 4% of the
// cycle, back to nothing by 14%, dark for the rest.
function pulse(phase) {
  if (phase < 0.04) return phase / 0.04;
  if (phase < 0.14) return 1 - (phase - 0.04) / 0.1;
  return 0;
}

// The flame paths use absolute M, C and Z only: that is how the brand repo
// writes them. Anything else means the artwork changed shape, and the hero
// bails out (the caller's catch) rather than draw a wrong flame.
function shapeFromPath(d) {
  const tokens = String(d).match(/[A-Za-z]|-?\d*\.?\d+(?:e[-+]?\d+)?/g);
  if (!tokens) throw new Error("empty flame path");
  // The flame's ink box is x 5.5..18.5, y 1.5..24: centre it, and flip SVG's
  // downward y into three's upward one.
  const X = (v) => v - 12;
  const Y = (v) => 12.75 - v;
  const shape = new THREE.Shape();
  let i = 0;
  const num = () => {
    const v = Number(tokens[i++]);
    if (!Number.isFinite(v)) throw new Error("bad flame path");
    return v;
  };
  const isCmd = (s) => /^[A-Za-z]$/.test(s);
  while (i < tokens.length) {
    const cmd = tokens[i++];
    if (cmd === "M") {
      const x = num();
      shape.moveTo(X(x), Y(num()));
    } else if (cmd === "C") {
      // C may repeat its coordinate triples without restating the letter.
      do {
        const [x1, y1, x2, y2, x, y] = [num(), num(), num(), num(), num(), num()];
        shape.bezierCurveTo(X(x1), Y(y1), X(x2), Y(y2), X(x), Y(y));
      } while (i < tokens.length && !isCmd(tokens[i]));
    } else if (cmd === "Z") {
      shape.closePath();
    } else {
      throw new Error(`unsupported flame path command ${cmd}`);
    }
  }
  return shape;
}

// A rounded-square slab lying flat, bottom face at y = 0, top at y = height.
// The bevel rounds every edge so the rim lights catch it.
function slab(width, radius, height, bevel, material) {
  const w = width - 2 * bevel;
  const r = Math.max(radius - bevel, 0.1);
  const s = new THREE.Shape();
  const h = w / 2;
  s.moveTo(-h + r, -h);
  s.lineTo(h - r, -h);
  s.quadraticCurveTo(h, -h, h, -h + r);
  s.lineTo(h, h - r);
  s.quadraticCurveTo(h, h, h - r, h);
  s.lineTo(-h + r, h);
  s.quadraticCurveTo(-h, h, -h, h - r);
  s.lineTo(-h, -h + r);
  s.quadraticCurveTo(-h, -h, -h + r, -h);
  const geo = new THREE.ExtrudeGeometry(s, {
    depth: height - 2 * bevel,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 3,
    curveSegments: 10,
  });
  // Extruded along +z; turn it so the extrusion runs up +y, then lift it so
  // the bottom bevel rests on y = 0.
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, bevel, 0);
  return new THREE.Mesh(geo, material);
}

function radial(size, stops) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  for (const [at, color] of stops) grad.addColorStop(at, color);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// The die's top face, painted as the SoC floorplan of templates/partials/
// soc.html: HBM interfaces down both sides, the harts and programmable
// compute along the top, FuryCore's tile array in the middle, SRAM and the
// I/O ring along the bottom. Two canvases: the colours, and the glow (what
// the ember emissive lights up: the tiles, brightest towards the centre).
function dieShot(size) {
  const base = document.createElement("canvas");
  const glow = document.createElement("canvas");
  base.width = base.height = glow.width = glow.height = size;
  const g = base.getContext("2d");
  const e = glow.getContext("2d");
  const u = size / 100; // the layout is in per cent of the die

  g.fillStyle = "#101115";
  g.fillRect(0, 0, size, size);
  e.fillStyle = "#000";
  e.fillRect(0, 0, size, size);

  const rect = (ctx, x, y, w, h, fill, stroke) => {
    ctx.fillStyle = fill;
    ctx.fillRect(x * u, y * u, w * u, h * u);
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 0.35 * u;
      ctx.strokeRect(x * u, y * u, w * u, h * u);
    }
  };

  // Metal routing: faint horizontal tracks in the channels between blocks.
  g.strokeStyle = "rgba(42,45,53,0.9)";
  g.lineWidth = 0.14 * u;
  for (let y = 4; y < 97; y += 1.6) {
    g.beginPath();
    g.moveTo(2 * u, y * u);
    g.lineTo(98 * u, y * u);
    g.stroke();
  }

  // Seal ring and bond pads round the edge.
  g.strokeStyle = COLOR.trace;
  g.lineWidth = 0.6 * u;
  g.strokeRect(1.2 * u, 1.2 * u, 97.6 * u, 97.6 * u);
  for (const k of PAD_C) {
    const p = 50 + (k / DIE) * 100;
    for (const [x, y] of [
      [p, 2.6],
      [p, 97.4],
      [2.6, p],
      [97.4, p],
    ]) {
      rect(g, x - 1.1, y - 1.1, 2.2, 2.2, "#4a505c");
    }
  }

  // HBM PHYs, with their lanes.
  for (const x of [5, 86]) {
    rect(g, x, 8, 9, 84, "#1d2027", "#383c46");
    g.fillStyle = "#2c3039";
    for (let y = 10; y < 90; y += 2.5) g.fillRect((x + 1.2) * u, y * u, 6.6 * u, 1.1 * u);
  }

  // Top row: Linux harts, RTOS harts, programmable compute.
  const cores = (x, y, w, h, cols, rows) => {
    const cw = (w - 2) / cols;
    const ch = (h - 2) / rows;
    for (let i = 0; i < cols; i++)
      for (let j = 0; j < rows; j++) {
        const cx = x + 1 + i * cw;
        const cy = y + 1 + j * ch;
        rect(g, cx + 0.4, cy + 0.4, cw - 0.8, ch - 0.8, "#22252d", "#3a3e48");
        rect(g, cx + 0.9, cy + 0.9, (cw - 1.8) * 0.45, (ch - 1.8) * 0.45, "#30343e");
      }
  };
  rect(g, 17, 6, 20, 22, "#1a1c22", "#3a3e48");
  cores(17, 6, 20, 22, 2, 2);
  rect(g, 39.5, 6, 21, 22, "#1a1c22", "#3a3e48");
  cores(39.5, 6, 21, 22, 3, 2);
  rect(g, 63, 6, 20, 22, "#1a1c22", "#3a3e48");
  g.fillStyle = "#2a2e37";
  for (let i = 0; i < 9; i++)
    for (let j = 0; j < 9; j++) g.fillRect((64 + i * 2.1) * u, (7.2 + j * 2.2) * u, 1.5 * u, 1.5 * u);

  // FuryCore's tile array, ember-lit, brightest towards the centre.
  rect(g, 17, 32, 66, 38, "#1b1311", COLOR.ember);
  const cols = 8;
  const rows = 3;
  const tw = 64 / cols;
  const th = 36 / rows;
  for (let i = 0; i < cols; i++)
    for (let j = 0; j < rows; j++) {
      const dx = (i - (cols - 1) / 2) / (cols / 2);
      const dy = (j - (rows - 1) / 2) / (rows / 2);
      const heat = Math.max(0.12, 0.95 - 0.55 * Math.hypot(dx, dy * 0.8) + 0.12 * Math.sin(i * 2.3 + j));
      const x = 18 + i * tw + 0.6;
      const y = 33 + j * th + 0.6;
      g.fillStyle = `rgba(232,65,15,${(0.25 + 0.6 * heat).toFixed(3)})`;
      g.fillRect(x * u, y * u, (tw - 1.2) * u, (th - 1.2) * u);
      e.fillStyle = `rgba(255,255,255,${(0.15 + 0.85 * heat).toFixed(3)})`;
      e.fillRect(x * u, y * u, (tw - 1.2) * u, (th - 1.2) * u);
    }

  // Bottom row: SRAM banks, then PCIe and Ethernet.
  rect(g, 17, 74, 42, 20, "#1d2027", "#3a3e48");
  g.fillStyle = "#2c3039";
  for (let i = 0; i < 4; i++) {
    for (let y = 75.5; y < 92.5; y += 1.2) g.fillRect((18 + i * 10.2) * u, y * u, 9 * u, 0.6 * u);
  }
  rect(g, 61, 74, 22, 20, "#1a1c22", "#3a3e48");
  for (let i = 0; i < 4; i++) rect(g, 62.5 + i * 5, 77, 3.6, 14, "#272a32", "#3a3e48");

  return { base: new THREE.CanvasTexture(base), glow: new THREE.CanvasTexture(glow) };
}

boot();
